import useSWR from "swr"
import { useMemo, useState } from "preact/hooks"
import { Card, CardContent, CardHeader } from "../../../components/ui/card"
import { getSkuSummary } from "../api/distributor_sales_api"
import { ChartMetric, SkuDataPoint } from "../types/distributor_sales"
import { DistributorSalesInteractivePie } from "./distributor_sales_interactive_pie"
import { availablePieMetrics, pieMetricLabel, PieMetricSelect } from "./distributor_sales_pie_shared"

interface SkuPieChartProps {
  source?: string
  reportId?: string
  className?: string
}

function toNumber(value: number | string | undefined): number {
  if (value === undefined) return 0
  if (typeof value === "string") return parseFloat(value) || 0
  return value
}

interface PieSlice {
  name: string
  value: number
}

function skuMetricValue(sku: SkuDataPoint, metric: ChartMetric): number {
  if (metric === "ordered_vendor_cost") return toNumber(sku.ordered_vendor_cost)
  if (metric === "shipped_vendor_cost") return toNumber(sku.shipped_vendor_cost)
  if (metric === "ordered_list_wholesale") return toNumber(sku.ordered_list_wholesale)
  if (metric === "shipped_list_wholesale") return toNumber(sku.shipped_list_wholesale)
  if (metric === "ordered_quantity") return sku.ordered_quantity ?? 0
  if (metric === "shipped_quantity") return sku.shipped_quantity ?? 0
  return sku.stores_ordered ?? 0
}

function buildPieSlices(skus: SkuDataPoint[], metric: ChartMetric): PieSlice[] {
  const getValue = (sku: SkuDataPoint): number => skuMetricValue(sku, metric)

  const sorted = [...skus].sort((a, b) => getValue(b) - getValue(a))
  return sorted
    .map((sku: SkuDataPoint) => ({ name: sku.sku, value: getValue(sku) }))
    .filter((s: PieSlice) => s.value > 0)
}

export function SkuPieChart({ source, reportId, className }: SkuPieChartProps) {
  const [metric, setMetric] = useState<ChartMetric>("shipped_vendor_cost")

  const { data, isLoading } = useSWR(
    ["sku-summary", source, reportId],
    () => getSkuSummary({ source: source as any, report_id: reportId }),
    { revalidateOnFocus: false }
  )

  const pieSlices = useMemo(
    () => (data?.skus ? buildPieSlices(data.skus, metric) : []),
    [data?.skus, metric]
  )

  if (isLoading) {
    return (
      <Card class={className}>
        <CardHeader><h3 class="text-lg font-semibold">Sales by SKU</h3></CardHeader>
        <CardContent><div class="h-[320px] flex items-center justify-center text-gray-400">Loading…</div></CardContent>
      </Card>
    )
  }

  if (!data?.skus?.length) {
    return (
      <Card class={className}>
        <CardHeader><h3 class="text-lg font-semibold">Sales by SKU</h3></CardHeader>
        <CardContent><div class="h-[320px] flex items-center justify-center text-gray-400">No item-level data available. Upload a Vendor Sales report to see SKU breakdown.</div></CardContent>
      </Card>
    )
  }

  return (
    <Card class={className}>
      <CardHeader>
        <div class="flex items-center justify-between gap-4">
          <h3 class="text-lg font-semibold">Sales by SKU</h3>
          <PieMetricSelect
            value={metric}
            onChange={setMetric}
            metrics={availablePieMetrics(data.skus, skuMetricValue, metric)}
          />
        </div>
        <p class="text-sm text-gray-500 mt-1">All SKUs · {pieMetricLabel(metric)}</p>
      </CardHeader>
      <CardContent>
        {pieSlices.length === 0 ? (
          <div class="h-[280px] flex items-center justify-center text-gray-400">
            This report has no {pieMetricLabel(metric).toLowerCase()} data. Pick another metric.
          </div>
        ) : (
          <DistributorSalesInteractivePie
            key={reportId ?? ""}
            slices={pieSlices}
            metric={metric}
            layout="card"
            rowClassName="min-h-[280px]"
            legendClassName="sm:min-w-[200px]"
          />
        )}
      </CardContent>
    </Card>
  )
}

if (import.meta.vitest) {
  describe("availablePieMetrics", () => {
    it("offers only metrics the report has, plus the current one", () => {
      // McLane: shipped vendor cost and units only.
      const mclane: SkuDataPoint[] = [{ sku: "A", shipped_vendor_cost: "25.20", shipped_quantity: 15 }]
      expect(availablePieMetrics(mclane, skuMetricValue, "shipped_vendor_cost")).toEqual([
        "shipped_vendor_cost",
        "shipped_quantity",
      ])
      expect(availablePieMetrics(mclane, skuMetricValue, "ordered_list_wholesale")).toContain("ordered_list_wholesale")
      // The API serializes absent metrics as null, not undefined.
      const with_nulls = [{ sku: "A", shipped_vendor_cost: "25.20", ordered_list_wholesale: null } as unknown as SkuDataPoint]
      expect(availablePieMetrics(with_nulls, skuMetricValue, "shipped_vendor_cost")).toEqual(["shipped_vendor_cost"])
    })
  })

  describe("buildPieSlices", () => {
    const skus: SkuDataPoint[] = [
      { sku: "A", shipped_vendor_cost: "500" },
      { sku: "B", shipped_vendor_cost: "300" },
      { sku: "C", shipped_vendor_cost: "200" },
    ]
    it("sorts by value descending", () => {
      const slices = buildPieSlices(skus, "shipped_vendor_cost")
      expect(slices[0].name).toBe("A")
      expect(slices[0].value).toBe(500)
    })
    it("includes every SKU as its own slice", () => {
      const many: SkuDataPoint[] = Array.from({ length: 15 }, (_, i) => ({
        sku: `SKU-${i}`,
        shipped_vendor_cost: String(100 - i),
      }))
      const slices = buildPieSlices(many, "shipped_vendor_cost")
      expect(slices.length).toBe(15)
      expect(slices.some((s: PieSlice) => s.name === "Other")).toBe(false)
    })
    it("uses stores_ordered for store count metric", () => {
      const skus: SkuDataPoint[] = [
        { sku: "A", stores_ordered: 5 },
        { sku: "B", stores_ordered: 3 },
      ]
      const slices = buildPieSlices(skus, "stores_ordered")
      expect(slices[0].name).toBe("A")
      expect(slices[0].value).toBe(5)
    })
  })
}
